/**
 * Vendas do sistema antigo (spec 009, secao 4; passagem/P01, 2.2): o historico do checkout proprio e da Guru, no servico
 * de pagamentos. Abre com a lista das mais recentes (Vitor, 17/09), com busca e filtros; SUPORTE pra cima ve tudo,
 * inclusive de quem nao esta mais ativo; cada pagina e busca fica registrada, e o registro so o OWNER ve. So leitura.
 */
import { Abas, Aviso, Botao, CabecalhoDePagina, Campo, CartaoDeVenda, Carregando, Dialogo, FONTE_DA_VENDA, Pilha, POR_PAGINA, STATUS_DA_VENDA, Segmentado, Selo, Tabela, TextoDeDocumento, Vazio, formatarDataHora, formatarReais, type VendaDoHistorico } from "@mtc/ui";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState, type FormEvent, type ReactNode } from "react";
import { erroDe, useApi, usePapel } from "../api";
import { config } from "../config";
import { usePagina } from "../pagina";

export const PAG = (caminho: string) => `${config().PAGAMENTOS_URL.replace(/\/$/, "")}${caminho}`;

type Documento = { id: string; tipo: "PLANO" | "GERAL"; categoria: string | null; titulo: string; versao: string; formato: "pdf" | "markdown"; conteudo: string; capturado_em: string | null };
type Consulta = { id: string; operador_id: string; acao: "BUSCA" | "LISTA" | "VENDA" | "DOCUMENTO"; termo: string; resultados: number | null; em: string };

/** Busca no historico (CPF, e-mail, nome, produto ou referencia). Cada busca que roda fica registrada no pagamentos. */
export function useBuscaDeVendas(busca: string) {
  const { ler } = useApi();
  const termo = busca.trim();
  return useQuery({ queryKey: ["vendas-do-legado", termo], queryFn: () => ler<{ itens: VendaDoHistorico[] }>(PAG(`/v1/admin/vendas-do-legado?busca=${encodeURIComponent(termo)}`)), enabled: termo.length >= 3 && Boolean(config().PAGAMENTOS_URL), staleTime: 60_000 });
}

/** O regulamento aceito numa venda: o PDF do sistema antigo na propria tela, ou o texto capturado do site. */
export function DocumentoDoHistorico({ id, aoFechar }: { id: string | null; aoFechar: () => void }) {
  const { ler } = useApi();
  const q = useQuery({ queryKey: ["documento-do-legado", id], queryFn: () => ler<Documento>(PAG(`/v1/admin/vendas-do-legado/documentos/${encodeURIComponent(id!)}`)), enabled: Boolean(id), staleTime: Infinity });
  const d = q.data;
  const url = useMemo(() => {
    if (!d || d.formato !== "pdf") return null;
    const bytes = Uint8Array.from(atob(d.conteudo), (c) => c.charCodeAt(0));
    return URL.createObjectURL(new Blob([bytes], { type: "application/pdf" }));
  }, [d]);
  useEffect(() => () => { if (url) URL.revokeObjectURL(url); }, [url]);
  let corpo: ReactNode;
  if (q.isLoading) corpo = <Carregando compacto texto="Abrindo o documento" />;
  else if (q.isError || !d) corpo = <Aviso tipo="erro">{erroDe(q.error)}</Aviso>;
  else if (url) corpo = <><iframe title={d.titulo} src={url} style={{ width: "100%", height: "65vh", border: "1px solid var(--borda)", borderRadius: "var(--r-2)" }} /><a className="mtc-link" href={url} target="_blank" rel="noreferrer">Abrir em outra aba</a></>;
  // O texto capturado do site, formatado como a pagina do regulamento (Vitor, 13/10), dentro da janela.
  else corpo = <div style={{ maxHeight: "65vh", overflowY: "auto", padding: "var(--e-4)", border: "1px solid var(--borda)", borderRadius: "var(--r-2)" }}><TextoDeDocumento texto={d.conteudo} titulo={d.titulo} /></div>;
  return (
    <Dialogo aberto={Boolean(id)} aoFechar={aoFechar} titulo={d ? `${d.titulo}, versão ${d.versao}` : "Regulamento"} descricao={d?.capturado_em ? `Texto capturado do site em ${formatarDataHora(d.capturado_em)}: o sistema antigo não guardava o arquivo.` : undefined}>
      <Pilha espaco={3}>{corpo}</Pilha>
    </Dialogo>
  );
}

export function PaginaVendasAntigas() {
  const { pode } = usePapel();
  const [aba, setAba] = useState<"vendas" | "consultas">("vendas");
  return (
    <Pilha espaco={5}>
      <CabecalhoDePagina titulo="Vendas antigas" descricao="O histórico de vendas do sistema antigo (checkout próprio e Guru), inclusive de quem não está mais ativo, da mais recente pra mais antiga. Compare com o plano antes de decidir. Toda consulta fica registrada." />
      {pode("OWNER") && <Abas rotulo="Vendas antigas" valor={aba} aoMudar={setAba} abas={[{ valor: "vendas", rotulo: "Vendas" }, { valor: "consultas", rotulo: "Consultas registradas" }]} />}
      {aba === "vendas" ? <ListaDeVendas /> : <Consultas />}
    </Pilha>
  );
}

type Fonte = "TODAS" | VendaDoHistorico["fonte"];
type Status = "TODOS" | VendaDoHistorico["status"];

/**
 * A lista (Vitor, 17/09): abre com as vendas mais recentes; busca e filtros estreitam. A venda abre o detalhe com os
 * aceites. De 20 em 20 com a paginacao de todas as tabelas, pagina no endereco (spec 058; antes, 50 e botoes proprios).
 */
function ListaDeVendas() {
  const { ler } = useApi();
  const [texto, setTexto] = useState("");
  const [busca, setBusca] = useState("");
  const [fonte, setFonte] = useState<Fonte>("TODAS");
  const [status, setStatus] = useState<Status>("TODOS");
  const [aberta, setAberta] = useState<VendaDoHistorico | null>(null);
  const [documento, setDocumento] = useState<string | null>(null);
  const pag = usePagina("pagina", [busca, fonte, status]);
  const qs = new URLSearchParams({ pagina: String(pag.pagina), limite: String(POR_PAGINA), ...(busca ? { busca } : {}), ...(fonte !== "TODAS" ? { fonte } : {}), ...(status !== "TODOS" ? { status } : {}) }).toString();
  const q = useQuery({ queryKey: ["vendas-do-legado", "lista", qs], queryFn: () => ler<{ itens: VendaDoHistorico[]; total: number; pagina: number; paginas: number }>(PAG(`/v1/admin/vendas-do-legado?${qs}`)), placeholderData: (anterior) => anterior, staleTime: 60_000 });
  const enviar = (ev: FormEvent) => { ev.preventDefault(); setBusca(texto.trim()); };
  const d = q.data;
  return (
    <Pilha espaco={4}>
      <form className="mtc-linha" style={{ gap: "var(--e-3)", alignItems: "flex-end", flexWrap: "wrap" }} onSubmit={enviar}>
        <div style={{ flex: "1 1 320px", minWidth: 0 }}>
          <Campo rotulo="CPF, e-mail, nome, produto ou referência" valor={texto} aoMudar={setTexto} placeholder="Ex.: 529.982.247-25" />
        </div>
        <Botao type="submit" disabled={texto.trim().length > 0 && texto.trim().length < 3}>Buscar</Botao>
        {busca && <Botao variante="fantasma" onClick={() => { setTexto(""); setBusca(""); }}>Limpar busca</Botao>}
      </form>
      <div className="mtc-linha" style={{ gap: "var(--e-4)", flexWrap: "wrap" }}>
        <Segmentado rotulo="Origem" nome="fonte-venda-antiga" valor={fonte} aoMudar={setFonte} opcoes={[{ valor: "TODAS", rotulo: "Todas" }, { valor: "GURU", rotulo: "Guru" }, { valor: "CHECKOUT_PROPRIO", rotulo: "Checkout próprio" }]} />
        <Segmentado rotulo="Situação" nome="status-venda-antiga" valor={status} aoMudar={setStatus} opcoes={[{ valor: "TODOS", rotulo: "Todas" }, { valor: "PAGA", rotulo: "Pagas" }, { valor: "ESTORNADA", rotulo: "Estornadas" }, { valor: "CONTESTADA", rotulo: "Contestadas" }]} />
      </div>
      {q.isLoading ? <Carregando compacto texto="Buscando no histórico" /> : q.isError ? <Aviso tipo="erro">{erroDe(q.error)}</Aviso> : !d?.itens.length ? (
        <Vazio titulo="Nenhuma venda">{busca ? `Nada no histórico com “${busca}”.` : "Nenhuma venda com esses filtros."}</Vazio>
      ) : (
        <Pilha espaco={3}>
          <span className="mtc-texto-2" style={{ fontSize: "var(--t-sm)" }}>{d.total} {d.total === 1 ? "venda" : "vendas"}{busca ? ` com “${busca}”` : ""}, da mais recente pra mais antiga.</span>
          <Tabela
            linhas={d.itens}
            paginacao={{ pagina: d.pagina, aoMudar: pag.aoMudar, total: d.total }}
            chave={(v) => v.id}
            cartao="media"
            colunas={[
              { chave: "quando", rotulo: "Pago em", celula: (v) => <span className="mtc-mono">{formatarDataHora(v.pago_em)}</span> },
              { chave: "comprador", rotulo: "Comprador", celula: (v) => <><strong>{v.comprador.nome ?? "Sem nome"}</strong>{v.comprador.cpf && <span className="mtc-tabela__apoio mtc-mono">{v.comprador.cpf}</span>}</> },
              { chave: "produto", rotulo: "Produto", celula: (v) => <>{v.produto}{v.itens.length > 0 && <span className="mtc-tabela__apoio">+ {v.itens.map((i) => i.produto).join(", ")}</span>}</> },
              { chave: "valor", rotulo: "Valor", alinhar: "direita", celula: (v) => <><span className="mtc-mono">{formatarReais(v.valor_centavos)}</span>{v.meio && <span className="mtc-tabela__apoio">{v.meio === "cartao" ? `cartão${v.parcelas && v.parcelas > 1 ? ` ${v.parcelas}x` : ""}` : v.meio.toUpperCase()}</span>}</> },
              { chave: "origem", rotulo: "Origem", celula: (v) => <><Selo tom={STATUS_DA_VENDA[v.status].tom}>{STATUS_DA_VENDA[v.status].rotulo}</Selo><span className="mtc-tabela__apoio">{FONTE_DA_VENDA[v.fonte]}</span></> },
              { chave: "acoes", rotulo: "Ações", rotuloOculto: true, celula: (v) => <Botao tamanho="pequeno" variante="fantasma" aria-label={`Ver a venda ${v.referencia}`} onClick={() => setAberta(v)}>Ver</Botao> },
            ]}
          />
        </Pilha>
      )}
      <Dialogo aberto={Boolean(aberta)} aoFechar={() => setAberta(null)} titulo="Venda do sistema antigo" descricao="Com os aceites dos regulamentos, quando existirem.">
        {aberta && <CartaoDeVenda venda={aberta} aoAbrirDocumento={setDocumento} />}
      </Dialogo>
      <DocumentoDoHistorico id={documento} aoFechar={() => setDocumento(null)} />
    </Pilha>
  );
}

const ACAO_DA_CONSULTA: Record<Consulta["acao"], string> = { BUSCA: "Buscou", LISTA: "Abriu a lista", VENDA: "Abriu a venda", DOCUMENTO: "Abriu o regulamento" };

function Consultas() {
  const { ler } = useApi();
  const q = useQuery({ queryKey: ["consultas-ao-legado"], queryFn: () => ler<{ itens: Consulta[] }>(PAG("/v1/admin/vendas-do-legado/consultas?limite=200")) });
  const operadores = useQuery({ queryKey: ["nomes-dos-administradores"], queryFn: () => ler<{ itens: { id: string; nome: string }[] }>("/v1/admin/administradores/nomes") });
  const nome = (id: string) => operadores.data?.itens.find((o) => o.id === id)?.nome ?? "Administrador";
  if (q.isLoading) return <Carregando compacto texto="Buscando o registro" />;
  if (q.isError) return <Aviso tipo="erro">{erroDe(q.error)}</Aviso>;
  return (
    <Tabela
      linhas={q.data?.itens ?? []}
      chave={(c) => c.id}
      vazio={<Vazio titulo="Nenhuma consulta">Ninguém buscou no histórico ainda.</Vazio>}
      colunas={[
        { chave: "quando", rotulo: "Quando", celula: (c) => <span className="mtc-mono">{formatarDataHora(c.em)}</span> },
        { chave: "quem", rotulo: "Quem", celula: (c) => nome(c.operador_id) },
        { chave: "o-que", rotulo: "O que", celula: (c) => <>{ACAO_DA_CONSULTA[c.acao]}<span className="mtc-tabela__apoio">{c.termo}</span></> },
        { chave: "resultados", rotulo: "Resultados", alinhar: "direita", celula: (c) => <span className="mtc-mono">{c.resultados ?? ""}</span> },
      ]}
    />
  );
}
